import { useParams } from 'react-router-dom';
import { useFieldData, FieldHub, FieldSample } from '@/services/field.service';
import { useTranslation } from 'react-i18next';
import { SproutIcon } from 'lucide-react';
import { useLayoutEffect, useRef, useState } from 'react';
import Sample from '../Sample/Sample';
import Hub from '../Device/Hub';
import { SampleModel } from '@/types/models';

// The page's columns: three where there's room, fewer on a narrower screen.
// Each hub spans as many as it has scanners (three at most), so hubs with
// fewer share a row; the unattached samples below use the same columns.
const GRID_COLUMNS = 'repeat(auto-fill, minmax(min(100%, 20rem), 1fr))';
const COLUMN_GAP = 24;
// Between hubs, and between the hubs and the unattached samples.
const GROUP_GAP = 32;
// The cable drops out of the centre of the 40px hub icon.
const TRUNK_X = 20;
const HUB_SIZE = 40;
const ELBOW_R = 8;
// The gap above each row of cards, where its branch of the cable runs, this
// far below the top of the gap: the rest is the cards' drops.
const ROW_GAP = 32;
const BRANCH_OFFSET = 10;
const PLUG_W = 10;
const PLUG_H = 7;
// Solid, so the cable doesn't show through: the brand green at 70% over the
// page background.
const PLUG_COLOR = '#69947f';
const CABLE = '#c5cdc4';

export default function FieldView() {
    const { projectId } = useParams();
    const { data: fieldData, error, isLoading } = useFieldData(projectId);
    const { t } = useTranslation();
    const [editingSampleId, setEditingSampleId] = useState<string | null>(null);
    // A hub with no sample has nothing to open: it is not listed.
    const hubs = (fieldData?.hubs ?? []).filter((hub) => hub.samples.length > 0);

    // How many columns the grid has at this width: a hub can't span more.
    const hubGridRef = useRef<HTMLDivElement>(null);
    const [columns, setColumns] = useState(3);
    useLayoutEffect(() => {
        const grid = hubGridRef.current;
        if (!grid) return;
        const measure = () =>
            setColumns(
                getComputedStyle(grid).gridTemplateColumns.split(' ').length,
            );
        measure();
        const observer = new ResizeObserver(measure);
        observer.observe(grid);
        return () => observer.disconnect();
    }, [fieldData]);

    if (isLoading)
        return <div className="text-sm text-[#7a8077]">{t('Loading...')}</div>;
    if (error)
        return (
            <div className="text-sm text-red-600">
                {t('Error loading field data')}
            </div>
        );

    if (
        !fieldData ||
        (hubs.length === 0 && fieldData.orphanedSamples.length === 0)
    ) {
        return (
            <div className="text-center py-16 bg-white border border-dashed border-[#dadbd5] rounded-xl">
                <SproutIcon className="h-10 w-10 text-[#cdd0c7] mx-auto mb-3" />
                <h3 className="text-base font-semibold text-[#1c2620]">
                    {t('No samples in this project yet')}
                </h3>
                <p className="text-sm text-[#7a8077] mt-1">
                    {t(
                        'Register a scanner and create your first sample to populate this view.',
                    )}
                </p>
            </div>
        );
    }

    if (!projectId)
        return (
            <div className="text-sm text-red-600">
                {t('Project ID is required')}
            </div>
        );

    return (
        <div className="flex flex-col" style={{ gap: GROUP_GAP }}>
            {hubs.length > 0 && (
                <div
                    ref={hubGridRef}
                    className="grid"
                    style={{
                        gridTemplateColumns: GRID_COLUMNS,
                        columnGap: COLUMN_GAP,
                        rowGap: GROUP_GAP,
                        // A one-scanner hub may fill the hole a wider one
                        // left at the end of a row.
                        gridAutoFlow: 'row dense',
                    }}
                >
                    {hubs.map((hub) => (
                        <FieldHubGroup
                            key={hub.macAddress}
                            hub={hub}
                            span={Math.min(hub.samples.length, columns)}
                            projectId={projectId}
                            editingSampleId={editingSampleId}
                            onEditSample={(s) => setEditingSampleId(s.id)}
                            onCancelEditSample={() => setEditingSampleId(null)}
                        />
                    ))}
                </div>
            )}

            {/* Samples on no hub are no longer acquiring: set apart at the
                bottom, with no cable. */}
            {fieldData.orphanedSamples.length > 0 && (
                <div
                    className="grid gap-y-3"
                    style={{
                        gridTemplateColumns: GRID_COLUMNS,
                        columnGap: COLUMN_GAP,
                    }}
                >
                    {fieldData.orphanedSamples.map((sample) => (
                        <FieldSampleCard
                            key={sample.id}
                            sample={sample}
                            projectId={projectId}
                            isEditing={editingSampleId === sample.id}
                            onEdit={(s) => setEditingSampleId(s.id)}
                            onCancel={() => setEditingSampleId(null)}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}

interface FieldHubGroupProps {
    hub: FieldHub;
    // How many of the page's columns it takes.
    span: number;
    projectId: string;
    editingSampleId: string | null;
    onEditSample: (sample: SampleModel) => void;
    onCancelEditSample: () => void;
}

/**
 * A hub and its samples: the hub icon above, the cards under it on the page's
 * own columns (a subgrid), and the cable drawn over them from where the cards
 * actually landed, as a tree: down from the hub icon, then in the gap above
 * each row a branch to the right, with a drop into the top of each of the
 * row's cards — each scanner on its own lead. The trunk stops at the last row,
 * each branch at its last card.
 */
function FieldHubGroup({
    hub,
    span,
    projectId,
    editingSampleId,
    onEditSample,
    onCancelEditSample,
}: FieldHubGroupProps) {
    const groupRef = useRef<HTMLDivElement>(null);
    const gridRef = useRef<HTMLDivElement>(null);
    // Per row: the height its branch runs at, its top, and its cards' drops
    // (x), at the middle of each.
    const [rows, setRows] = useState<
        { y: number; top: number; drops: number[] }[]
    >([]);

    useLayoutEffect(() => {
        const group = groupRef.current;
        const grid = gridRef.current;
        if (!group || !grid) return;
        const measure = () => {
            const origin = group.getBoundingClientRect();
            const byTop = new Map<number, { y: number; top: number; drops: number[] }>();
            for (const card of Array.from(grid.children)) {
                const r = card.getBoundingClientRect();
                const top = r.top - origin.top;
                const x = r.left - origin.left + r.width / 2;
                // Cards of a row share their top.
                const key = Math.round(top);
                const row = byTop.get(key);
                if (row) row.drops.push(x);
                else
                    byTop.set(key, {
                        y: top - ROW_GAP + BRANCH_OFFSET,
                        top,
                        drops: [x],
                    });
            }
            const next = [...byTop.values()];
            for (const row of next) row.drops.sort((a, b) => a - b);
            setRows(next);
        };
        measure();
        const observer = new ResizeObserver(measure);
        observer.observe(grid);
        for (const card of Array.from(grid.children)) observer.observe(card);
        return () => observer.disconnect();
    }, [hub.samples, editingSampleId, span]);

    const last = rows[rows.length - 1];
    const R = ELBOW_R;
    const stroke = { stroke: CABLE, strokeWidth: 2, fill: 'none' };

    return (
        <div
            ref={groupRef}
            className="relative grid content-start"
            style={{
                gridColumn: `span ${span}`,
                gridTemplateColumns: 'subgrid',
            }}
        >
            {last && (
                <svg
                    className="pointer-events-none absolute inset-0 h-full w-full overflow-visible"
                    aria-hidden
                >
                    <path d={`M ${TRUNK_X} ${HUB_SIZE} V ${last.y - R}`} {...stroke} />
                    {rows.map((row) => {
                        const end = row.drops[row.drops.length - 1];
                        return (
                            <g key={row.top}>
                                {/* Off the trunk, along the row, down into
                                    its last card: rounded both ways. */}
                                <path
                                    d={`M ${TRUNK_X} ${row.y - R} Q ${TRUNK_X} ${row.y} ${TRUNK_X + R} ${row.y} H ${end - R} Q ${end} ${row.y} ${end} ${row.y + R} V ${row.top - PLUG_H}`}
                                    {...stroke}
                                />
                                {row.drops.slice(0, -1).map((x) => (
                                    <path
                                        key={x}
                                        d={`M ${x} ${row.y} V ${row.top - PLUG_H}`}
                                        {...stroke}
                                    />
                                ))}
                                {row.drops.map((x) => (
                                    <rect
                                        key={x}
                                        x={x - PLUG_W / 2}
                                        y={row.top - PLUG_H}
                                        width={PLUG_W}
                                        height={PLUG_H}
                                        rx={2}
                                        fill={PLUG_COLOR}
                                    />
                                ))}
                            </g>
                        );
                    })}
                </svg>
            )}
            <div className="col-span-full">
                <Hub />
            </div>
            <div
                ref={gridRef}
                className="relative col-span-full grid"
                style={{
                    gridTemplateColumns: 'subgrid',
                    rowGap: ROW_GAP,
                    paddingTop: ROW_GAP,
                }}
            >
                {hub.samples.map((sample) => (
                    <FieldSampleCard
                        key={sample.id}
                        sample={sample}
                        projectId={projectId}
                        isEditing={editingSampleId === sample.id}
                        onEdit={onEditSample}
                        onCancel={onCancelEditSample}
                    />
                ))}
            </div>
        </div>
    );
}

interface FieldSampleCardProps {
    sample: FieldSample;
    projectId: string;
    isEditing: boolean;
    onEdit: (sample: SampleModel) => void;
    onCancel: () => void;
}

function FieldSampleCard({
    sample,
    projectId,
    isEditing,
    onEdit,
    onCancel,
}: FieldSampleCardProps) {
    const sampleModel: SampleModel = {
        id: sample.id,
        name: sample.name,
        description: sample.description,
        projectId: sample.projectId,
        scannerId: sample.scannerSerialNumber,
        rotateClockwise: sample.rotateClockwise,
        topEdgeDepthCm: sample.topEdgeDepthCm,
        tiltDeg: sample.tiltDeg,
        latitude: sample.latitude,
        longitude: sample.longitude,
        softDelete: sample.softDelete,
        createdAt: new Date(sample.createdAt),
        updatedAt: new Date(sample.updatedAt),
        scanner: sample.scanner
            ? {
                  serialNumber: sample.scanner.serialNumber,
                  projectId: sample.scanner.projectId,
                  hubId: sample.scanner.hubId,
                  mode: sample.scanner.mode,
                  t: sample.scanner.t,
                  x: sample.scanner.x,
                  y: sample.scanner.y,
                  l: sample.scanner.l,
                  resolutionDpi: sample.scanner.resolutionDpi,
                  quality: sample.scanner.quality,
                  cropTopMm: sample.scanner.cropTopMm,
                  cropBottomMm: sample.scanner.cropBottomMm,
                  cropLeftMm: sample.scanner.cropLeftMm,
                  cropRightMm: sample.scanner.cropRightMm,
                  createdAt: new Date(sample.scanner.createdAt),
                  updatedAt: new Date(sample.scanner.updatedAt),
              }
            : undefined,
    };

    return (
        <Sample
            sample={sampleModel}
            projectId={projectId}
            isEditing={isEditing}
            onEdit={onEdit}
            onCancel={onCancel}
        />
    );
}
